import React, { useState, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FaPaperclip,FaDownload,FaTrash } from 'react-icons/fa';

// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"></path>
  </svg>
);

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);

const API_URL = '/api';

// --- SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.IssueHeaderNo || option.SiteIndentNo || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.IssueHeaderID || option.SiteIndentID || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.ProjectNo || option.ProjectCode || option.id || "",
  disabled = false,
  className = "",
  isLoading = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom, left: rect.left, width: rect.width });
      }
    };
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    return () => { window.removeEventListener('resize', handleUpdatePosition); window.removeEventListener('scroll', handleUpdatePosition); };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) items[highlightedIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => { searchInputRef.current.focus(); }, 0);
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

    const handleKeyDown = (e) => {
    // ADD THIS CHECK: If the dropdown is disabled, stop any keyboard interaction
    if (disabled) return;

    if (!isOpen) { 
      if (e.key === 'ArrowDown' || e.key === 'Enter') { 
        setIsOpen(true); 
        return; 
      } 
      return; 
    }
    
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); setHighlightedIndex(prev => { if (prev < filteredOptions.length - 1) return prev + 1; else if (filteredOptions.length > 0) return 0; return prev; }); break;
      case 'ArrowUp': e.preventDefault(); setHighlightedIndex(prev => { if (prev > 0) return prev - 1; else if (filteredOptions.length > 0) return filteredOptions.length - 1; return prev; }); break;
      case 'Enter': e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) handleSelectOption(filteredOptions[highlightedIndex]); break;
      case 'Escape': setIsOpen(false); break;
      default: break;
    }
  };

  const handleSelectOption = (option) => { onChange(getOptionValue(option)); setIsOpen(false); setSearchTerm(''); setHighlightedIndex(-1); };
  useEffect(() => { const handleClickOutside = (event) => { if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false); }; document.addEventListener('mousedown', handleClickOutside); return () => { document.removeEventListener('mousedown', handleClickOutside); }; }, []);
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        style={{ borderColor: error ? 'rgb(248, 113, 113)' : 'rgb(230, 217, 204)' }}
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
           {isLoading && <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full mr-2 inline-block"></div>}
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// --- MAIN FORM COMPONENT ---
const MaterialReceiveFormModal = ({ open, onClose, initialData, onSubmit ,  onFormSuccess}) => {
  
  // --- STATE ---
  const initialHeader = {
    ReceiveHeaderID: null,
    ReceiveHeaderNo: '',
    IssueHeaderID: null,
    IssueHeaderNo: '',
    SiteIndentID: null,
    SiteIndentNo: '',
    ReceiveDate: new Date().toISOString().split('T')[0],
    VendorCode: '', 
    SurveyBOQID: '', 
    ProjectHeaderId: '', 
    ProjectNo: '',
    FromFeederId: '', 
    ToFeederId: '', 
    Circle: '', 
    District: '', 
    Division: '', 
    SubDivision: '', 
    SubStation: '', 
    ReceiverName: '',
    Remarks: '',
    BcSuccess: false,
    ReceiveSource: 'Issue', // Default
    ReceiveSourceId: null,
     ReceiveSourceNo: '',
      ActivityId: null,
    SubActivityId: null,
     Attachment: null,           // ✅ ADD THIS
    AttachmentFileName: null,
  };

  const initialLine = {
    SiteIndentLineID: null,
    SurveyBOQLineID: null,
    IssueLineID: null,
    ProductCode: '',
    FeederId: '',
    TaskNo: '',
    Description: '',
    Remark: '',
    UnitOfMeasure: 'NOS',
    SiteIndentQty: 0,
    IssuedQty: 0,
    Quantity: 0,
    QtyToReceive: 0,
    ReceiveQty: 0,
    IssueLineNumber: 100,
    RemainingQty: 0,
    OriginalQtyToReceive: 0,
    BcSuccess: false,
    ReceiveSource: 'Issue',
    ReceiveSourceId: null,
    ActivityId: null,      // ADD THIS
    SubActivityId: null 
  };

  const [header, setHeader] = useState(initialHeader);
  const [lines, setLines] = useState([initialLine]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  // Dropdown Data
  const [projects, setProjects] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [materialIssues, setMaterialIssues] = useState([]); 
  const [surveyBOQs, setSurveyBOQs] = useState([]); 
  const [siteIndents, setSiteIndents] = useState([]); 
  const [materialReturns, setMaterialReturns] = useState([]); 
  const [nextReceiveNumber, setNextReceiveNumber] = useState('');
   const [projectActivities, setProjectActivities] = useState([]);
  
  // Receive Type State
  const [receiveType, setReceiveType] = useState('Issue'); // 'Issue' or 'Return'

   const headerFileInputRef = useRef(null);

  // ADD THIS STATE:
  const [highlightedRows, setHighlightedRows] = useState([]);
  const isHeaderLocked = header.BcSuccess === true || header.BcSuccess === 1;
  

    // --- TOAST / ALERT STATE ---
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

  // Refs for Resizing
  const [columnWidths, setColumnWidths] = useState({
    lineNo: 80, productCode: 180, taskNo: 100, feeder: 100, desc: 280,
    siteQty: 200, issuedQty: 200, remainingQty: 200, qtyToReceive: 200, uom: 80, action: 80,
    remark:280,activity: 200,    
    subActivity: 200
  });
  const resizingRef = useRef(null);

  // --- EFFECTS ---
  useEffect(() => {
    const initializeData = async () => {
      if (open) {
        setIsMaximized(false);
        resetForm();
        fetchProjects();
        fetchFeeders();
        fetchVendors();
        fetchSurveyBOQs(); 
        fetchSiteIndents(); 
        fetchMaterialReturns();
        fetchFilteredMaterialIssues();

        const isEditMode = initialData && initialData.header && initialData.header.ReceiveHeaderID;
        if (!isEditMode) {
          fetchNextReceiveNumber();
        }

        if (initialData) {
          const sourceData = initialData.header ? initialData.header : initialData;
          
          const currentReceiveType = sourceData.ReceiveSource || 'Issue';
          setReceiveType(currentReceiveType);

           const projectNo = sourceData.ProjectNo;
          if (projectNo) {
            fetchActivities(projectNo);
          }

           const formattedActivity = sourceData.ActivityCode 
            ? `${sourceData.ActivityCode} - ${sourceData.ActivityDesc || ''}` 
            : '';
          const formattedSubActivity = sourceData.SubActivityCode 
            ? `${sourceData.SubActivityCode} - ${sourceData.SubActivityDesc || ''}` 
            : '';
          
          setHeader(prev => ({ ...initialHeader, ...sourceData, ReceiveDate: sourceData.ReceiveDate ? sourceData.ReceiveDate.split('T')[0] : new Date().toISOString().split('T')[0],
            ActivityName: formattedActivity,
            SubActivityName: formattedSubActivity
           }));
          
          let editLines = [];
          if (sourceData.Lines) editLines = sourceData.Lines;
          else if (initialData.lines) editLines = initialData.lines;
          
          if (editLines.length > 0) {
             // UPDATE: Logic to determine which remaining qty to fetch
             const linesWithRemaining = await Promise.all(editLines.map(async (l) => {
               let remQty = 0;
               
               if (currentReceiveType === 'Return') {
                 // Fetch Return Remaining Qty
                 remQty = await fetchReturnLineRemainingQty(sourceData.ReceiveSourceId, l.ReceiveSourceId);
               } else {
                 // Fetch Issue Remaining Qty (Default)
                 remQty = await fetchIssueLineRemainingQty(sourceData.IssueHeaderID, l.IssueLineID);
               }

               return {
                 ...l,
                 RemainingQty: remQty || 0,
                 OriginalQtyToReceive: parseFloat(l.QtyToReceive) || 0
               };
             }));
             setLines(linesWithRemaining);
          } else {
            setLines([initialLine]);
          }
        }
      }
    };

    initializeData();
  }, [open, initialData]);

  // --- DYNAMIC FILTERING EFFECT ---
   // --- DYNAMIC FILTERING EFFECT ---
  useEffect(() => {
    // Decide which function to call based on receiveType
    if (receiveType === 'Issue') {
      fetchFilteredMaterialIssues();
    } else if (receiveType === 'Return') {
      fetchMaterialReturns();
    }
  }, [header.VendorCode, header.ProjectNo, header.FromFeederId, header.ToFeederId, header.SurveyBOQID, header.SiteIndentID, receiveType]);

  // --- API HANDLERS --
  
  const fetchIssueLineRemainingQty = async (headerId, lineId) => {
    if (!headerId || !lineId) return 0;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/material-issue/${headerId}/line/${lineId}/remaining-qty`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return 0;
      const data = await res.json();
      return data.data ? (data.data.RemainingQty || 0) : 0;
    } catch (e) {
      return 0;
    }
  };

    const fetchReturnLineRemainingQty = async (returnId, lineId) => {
    if (!returnId || !lineId) return 0;
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/material-return/${returnId}/line/${lineId}/remaining-qty`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return 0;
      const data = await res.json();
      return data.data ? (data.data.RemainingQty || 0) : 0;
    } catch (e) {
      return 0;
    }
  };

  const fetchNextReceiveNumber = async () => {
    try {
      const res = await fetch(`${API_URL}/material-receive/next-number`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await res.json();
      if (data && data.nextHeaderNo) setNextReceiveNumber(data.nextHeaderNo);
    } catch (e) {  setNextReceiveNumber('MR-1'); }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/vendors`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setVendors(Array.isArray(data) ? data : []);
    } catch (e) {  setVendors([]); }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/project-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {  setProjects([]); }
  };

  const fetchFeeders = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/feeder-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setFeeders(Array.isArray(data) ? data : []);
    } catch (e) {  setFeeders([]); }
  };

    const fetchSurveyBOQs = async () => {
    try {
      const token = localStorage.getItem('authToken'); 
      const res = await fetch(`${API_URL}/survey-boq-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setSurveyBOQs(Array.isArray(data) ? data : []);
    } catch (e) { 
      setSurveyBOQs([]); 
    }
  };

   const fetchActivities = async (projectNo) => {
    if (!projectNo) {
      setProjectActivities([]);
      return;
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/activity-master/project/${projectNo}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch activities');
      const data = await res.json();
      setProjectActivities(data || []);
    } catch (e) {
      console.error("Error fetching activities:", e);
      setProjectActivities([]);
    }
  };

   useEffect(() => {
    if (header.ProjectNo) {
      fetchActivities(header.ProjectNo);
    } else {
      setProjectActivities([]);
    }
  }, [header.ProjectNo]);

    const fetchSiteIndents = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/site-indent-filter`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setSiteIndents(Array.isArray(data) ? data : []);
    } catch (e) {  setSiteIndents([]); }
  };

const fetchMaterialReturns = async () => {
  setFilterLoading(true); // Ensure loading starts
  try {
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams();

    // 1. Add Filters (Same as before)
    if(header.VendorCode) params.append('vendorCode', header.VendorCode);
    if(header.ProjectNo) params.append('projectNo', header.ProjectNo);
    if(header.SurveyBOQID) params.append('surveyBoqId', header.SurveyBOQID);
    if(header.FromFeederId) params.append('fromFeederId', header.FromFeederId);
    if(header.ToFeederId) params.append('toFeederId', header.ToFeederId);

    // 2. Handle Edit Mode (MATCHING THE ISSUE LOGIC EXACTLY)
    const isEditMode = initialData && initialData.header && initialData.header.ReceiveHeaderID;
    
    // CHANGE: Remove the 'ReceiveSource' check. Just check if ID exists.
    // This matches how you check 'initialData.header.IssueHeaderID' for Issues.
    if (isEditMode && initialData.header.ReceiveSourceId) {
      params.append('currentReturnHeaderId', initialData.header.ReceiveSourceId);
    }

    // 3. API Call
    const res = await fetch(`${API_URL}/material-return-filter-receive?${params.toString()}`, { 
      headers: { 'Authorization': `Bearer ${token}` } 
    });
    
    const data = await res.json();
    setMaterialReturns(Array.isArray(data) ? data : []);
  } catch (e) {  
    setMaterialReturns([]); 
  } finally {
    setFilterLoading(false); 
  }
};

  const fetchFilteredMaterialIssues = async () => {
    setFilterLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (header.VendorCode) params.append('vendorCode', header.VendorCode);
      if (header.ProjectNo) params.append('projectNo', header.ProjectNo);
      if (header.FromFeederId) params.append('fromFeederId', header.FromFeederId);
      if (header.ToFeederId) params.append('toFeederId', header.ToFeederId);
      if (header.SurveyBOQID) params.append('surveyBoqId', header.SurveyBOQID);
       if (header.SiteIndentID) params.append('siteIndentID', header.SiteIndentID);

       const isEditMode = initialData && initialData.header && initialData.header.ReceiveHeaderID;
      if (isEditMode && initialData.header.IssueHeaderID) {
        params.append('currentIssueHeaderId', initialData.header.IssueHeaderID);
      }
      

      const res = await fetch(`${API_URL}/material-issue-filter-Receive-data?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMaterialIssues(Array.isArray(data) ? data : []);
    } catch (e) {
      setMaterialIssues([]);
    } finally {
      setFilterLoading(false);
    }
  };

  // --- RECEIVE TYPE HANDLER ---
  const handleReceiveTypeChange = (type) => {
    setReceiveType(type);
    // Reset header and lines when switching type
    setHeader(prev => ({
      ...initialHeader,
      ReceiveSource: type,
      ReceiveSourceId: null,
      ReceiveDate: prev.ReceiveDate
    }));
    setLines([initialLine]);
  };

  // --- MATERIAL ISSUE SELECTION LOGIC ---
  const handleMaterialIssueChange = async (issueHeaderID) => {
    const selectedIssue = materialIssues.find(mi => mi.IssueHeaderID == issueHeaderID);

    if (selectedIssue) {
      setHeader(prev => ({
        ...prev,
        ReceiveSource: 'Issue',
        ReceiveSourceId: selectedIssue.IssueHeaderID,
        ReceiveSourceNo: selectedIssue.IssueHeaderNo,
        IssueHeaderID: selectedIssue.IssueHeaderID,
        IssueHeaderNo: selectedIssue.IssueHeaderNo,
        SiteIndentID: selectedIssue.SiteIndentID,
        SiteIndentNo: selectedIssue.SiteIndentNo || '',
        SurveyBOQID: selectedIssue.SurveyBOQID || '', 
        ProjectNo: selectedIssue.ProjectNo,
        ProjectHeaderId: selectedIssue.ProjectHeaderId,
        VendorCode: selectedIssue.VendorCode,
        FromFeederId: selectedIssue.FromFeederId,
        ToFeederId: selectedIssue.ToFeederId,
        Circle: selectedIssue.Circle || '',
        District: selectedIssue.District || '',
        Division: selectedIssue.Division || '', 
        SubDivision: selectedIssue.SubDivision || '', 
        SubStation: selectedIssue.SubStation || '',
        ActivityId: selectedIssue.ActivityId || null,
        SubActivityId: selectedIssue.SubActivityId || null,
        ActivityName: selectedIssue.ActivityCode 
          ? `${selectedIssue.ActivityCode} - ${selectedIssue.ActivityDesc || ''}` 
          : '',
        SubActivityName: selectedIssue.SubActivityCode 
          ? `${selectedIssue.SubActivityCode} - ${selectedIssue.SubActivityDesc || ''}` 
          : ''
      }));

      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_URL}/material-issue-filter-Receive/${selectedIssue.IssueHeaderID}`, {
           headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch Issue Details');
        const issueData = await res.json();

        if (issueData.lines && Array.isArray(issueData.lines)) {
           const linesWithRemaining = await Promise.all(issueData.lines.map(async (issueLine, index) => {
             const remQty = await fetchIssueLineRemainingQty(selectedIssue.IssueHeaderID, issueLine.IssueLineID);
             
             return {
               ...initialLine,
               ReceiveSource: 'Issue',
               ReceiveSourceId: issueLine.IssueLineID,
               IssueLineID: issueLine.IssueLineID,
               SiteIndentLineID: issueLine.SiteIndentLineID || null,
               SurveyBOQLineID: issueLine.SurveyBOQLineID || null,
               ProductCode: issueLine.ProductCode,
               Description: issueLine.Description,
               TaskNo: issueLine.TaskNo || '',
               UnitOfMeasure: issueLine.UnitOfMeasure || 'NOS',
               
               SiteIndentQty: issueLine.SiteIndentQty || 0, 
               IssuedQty: issueLine.QtyToIssue || issueLine.QuantityIssued || 0, 
               Quantity: issueLine.Quantity || 0,
               
               QtyToReceive: remQty >= 0 ? remQty : 0, 
               ReceiveQty: 0,
               
               RemainingQty: remQty > 0 ? remQty : 0,
               OriginalQtyToReceive: 0, 
               
               IssueLineNumber: issueLine.IssueLineNumber || (100 + (index * 100)),
               ActivityId: issueLine.ActivityId || null,
               SubActivityId: issueLine.SubActivityId || null
             };
           }));
           
           setLines(linesWithRemaining);
        } else {
          setLines([initialLine]);
        }
      } catch (err) {
        showToast('error', 'Load Error', 'Failed to load lines for the selected Material Issue. Please try again.');
      }
    }
  };

  // --- MATERIAL RETURN SELECTION LOGIC ---
   // --- MATERIAL RETURN SELECTION LOGIC ---
    const handleMaterialReturnChange = async (returnHeaderID) => {
    const selectedReturn = materialReturns.find(mr => mr.MaterialReturnID == returnHeaderID);

    if (selectedReturn) {
      setLines([initialLine]); // Clear lines optimistically

      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${API_URL}/material-return/${selectedReturn.MaterialReturnID}`, {
           headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch Return Details');
        const returnData = await res.json();
        
        const returnHeader = returnData.header;

        setHeader(prev => ({
          ...prev,
          ReceiveSource: 'Return',
          ReceiveSourceId: returnHeader.MaterialReturnID,
          ReceiveSourceNo: returnHeader.MaterialReturnNo,
          ProjectNo: returnHeader.ProjectNo || '', 
          ProjectHeaderId: returnHeader.ProjectHeaderId || '',
          FromFeederId: returnHeader.FromFeederId || '', 
          ToFeederId: returnHeader.ToFeederId || '', 
          VendorCode: returnHeader.VendorCode || '',
          IssueHeaderID: null,
          IssueHeaderNo: '',
          SiteIndentID: null,
          SiteIndentNo: '',
          SurveyBOQID: returnHeader.SurveyBOQID || '', 
          Circle: '',
          District: '',
          Division: '',
          SubDivision: '',
          SubStation: '',
          ActivityId: null,
          SubActivityId: null,
          ActivityName: '',
          SubActivityName: ''
        }));

        if (returnData.lines && Array.isArray(returnData.lines)) {
           // UPDATE: Fetch remaining qty for return lines
           const linesWithRemaining = await Promise.all(returnData.lines.map(async (rLine, index) => {
             const remQty = await fetchReturnLineRemainingQty(returnHeader.MaterialReturnID, rLine.MaterialReturnLineID);
             
             return {
               ...initialLine,
               ReceiveSource: 'Return',
               ReceiveSourceId: rLine.MaterialReturnLineID,
               IssueLineID: null, 
               
               ProductCode: rLine.ProductCode,
               Description: rLine.Description,
               TaskNo: rLine.TaskNo || '',
               UnitOfMeasure: rLine.UnitOfMeasure || 'NOS',
               
               // Display original return qty in Indent/Issued columns for reference
               SiteIndentQty: rLine.Quantity || 0, 
               IssuedQty: rLine.Quantity || 0,    
               
               // Logic: Default QtyToReceive to the Remaining Qty
               RemainingQty: remQty || 0,
               QtyToReceive: remQty || 0, 
               OriginalQtyToReceive: 0, // Set to 0 for new selection, or handle edit mode logic
               
               IssueLineNumber: rLine.LineNumber || (100 + (index * 100)),
                ActivityId: rLine.ActivityId || null,
               SubActivityId: rLine.SubActivityId || null
             };
           }));
           setLines(linesWithRemaining);
        } else {
          setLines([initialLine]);
        }
      } catch (err) {
        console.error("Error fetching return lines", err);
        showToast('error', 'Load Error', 'Failed to load lines for the selected Material Return.');
      }
    }
  };

  const addLine = () => {
    const lastLine = lines[lines.length - 1];
    const nextLineNumber = lastLine ? parseInt(lastLine.IssueLineNumber) + 100 : 100;
    setLines([...lines, { ...initialLine, IssueLineNumber: nextLineNumber }]);
  };
  
  const removeLine = (index) => {
    if (lines.length > 1) setLines(lines.filter((_, i) => i !== index));
  };

  const handleLineChange = (index, field, value) => {
    if (field === 'QtyToReceive') {
      const line = lines[index];
      const originalQty = parseFloat(line.OriginalQtyToReceive) || 0;
      const remainingQty = parseFloat(line.RemainingQty) || 0;
      let newVal = parseFloat(value);

      if (isNaN(newVal)) newVal = 0;

      const difference = newVal - originalQty;

      if (difference > remainingQty) {
        const maxAllowed = originalQty + remainingQty;
        showToast(`Cannot exceed remaining quantity. Max Allowed: ${maxAllowed.toFixed(6)} .`, 'error', 'Validation Error');
        newVal = maxAllowed;
      }
      
      if (newVal < 0) newVal = 0;

      const newLines = [...lines];
      newLines[index][field] = newVal;
      setLines(newLines);
      return;
    }

    const newLines = [...lines];
    newLines[index][field] = value;
    setLines(newLines);
  };

  const resetForm = () => {
    setHeader(initialHeader);
    setLines([initialLine]);
    setNextReceiveNumber('');
    setReceiveType('Issue');
  };

  const startResize = (columnKey, startX) => {
    resizingRef.current = { columnKey, startX, startWidth: columnWidths[columnKey] };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };
  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    setColumnWidths(prev => ({ ...prev, [columnKey]: Math.max(60, startWidth + delta) }));
  };
  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

    // ✅ ADD THESE HANDLERS

  const handleHeaderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeader(prev => ({
        ...prev,
        AttachmentFileName: file.name,
        Attachment: file.name // temporary indicator
      }));
    }
  };

  const handleRemoveHeaderAttachment = () => {
    setHeader(prev => ({
      ...prev,
      Attachment: null,
      AttachmentFileName: ""
    }));
    if (headerFileInputRef.current) {
      headerFileInputRef.current.value = "";
    }
  };

  const handleDownloadHeaderAttachment = async () => {
    try {
      if (!header.ReceiveHeaderID) {
        showToast("Receive ID not found", "error");
        return;
      }
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_URL}/material-receive/${header.ReceiveHeaderID}/header/file`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error("Failed to download file");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = header.AttachmentFileName || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showToast("Error downloading file", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!header.ReceiveSourceId) {
      let msg = receiveType === 'Issue' ? 'Material Issue is required.' : 'Material Return is required.';
      showToast(msg, 'error', 'Required Field');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('authToken');

      let attachmentBase64 = null;
      if (headerFileInputRef.current && headerFileInputRef.current.files[0]) {
          attachmentBase64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result); // result is data URL (base64)
              reader.readAsDataURL(headerFileInputRef.current.files[0]);
          });
      }
      
      const processedLines = lines.map(l => ({
        ...l,
        SiteIndentLineID: l.SiteIndentLineID ? parseInt(l.SiteIndentLineID) : null,
        IssueLineID: l.IssueLineID ? parseInt(l.IssueLineID) : null,
        FeederId: l.FeederId ? parseInt(l.FeederId) : null,
        SiteIndentQty: parseFloat(l.SiteIndentQty) || 0,
        IssuedQty: parseFloat(l.IssuedQty) || 0,
        QtyToReceive: parseFloat(l.QtyToReceive) || 0,
        ReceiveQty: parseFloat(l.ReceiveQty) || 0, 
        IssueLineNumber: parseInt(l.IssueLineNumber) || 0,
        ReceiveSource: receiveType,
        ReceiveSourceId: l.ReceiveSourceId ? parseInt(l.ReceiveSourceId) : null,
         ReceiveSourceNo: header.ReceiveSourceNo || null,
      }));

      const payload = {
        header: {
          ...header,
          ReceiveSource: receiveType,
          ReceiveSourceId: header.ReceiveSourceId ? parseInt(header.ReceiveSourceId) : null,
          ReceiveHeaderNo: (initialData && initialData.header && initialData.header.ReceiveHeaderID) ? header.ReceiveHeaderNo : nextReceiveNumber,
          IssueHeaderID: header.IssueHeaderID ? parseInt(header.IssueHeaderID) : null,
          SurveyBOQID: header.SurveyBOQID ? parseInt(header.SurveyBOQID) : null, 
          ProjectHeaderId: header.ProjectHeaderId ? parseInt(header.ProjectHeaderId) : null,
          FromFeederId: header.FromFeederId ? parseInt(header.FromFeederId) : null,
          ToFeederId: header.ToFeederId ? parseInt(header.ToFeederId) : null,
          ...(attachmentBase64 ? {
              Attachment: attachmentBase64,
              AttachmentFileName: headerFileInputRef.current.files[0].name
          } : {})
        },
        lines: processedLines
      };

      const sourceData = initialData && initialData.header ? initialData.header : initialData;
      const editId = sourceData ? sourceData.ReceiveHeaderID : null;

      const isEditMode = !!editId;
      const url = isEditMode ? `${API_URL}/material-receive/${editId}` : `${API_URL}/material-receive`;
      const method = isEditMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
          const errorData = await res.json();
          const error = new Error(errorData.message || 'Failed to save data.');
          error.errors = errorData.errors || null;
          throw error;
      }
              const result = await res.json();
       if (onFormSuccess) { 
        onFormSuccess(result, isEditMode ? 'Updated' : 'Created');
      }
       onClose();

    } catch (error) {
        if (error.message && error.message.includes('exceeds remaining quantity') && error.errors) {
            const newLines = [...lines];
            const newHighlightedRows = [];
            
            error.errors.forEach(err => {
                // UPDATED LOGIC: Check for IssueLineID OR ReceiveSourceId
                const lineIndex = newLines.findIndex(l => 
                    (l.IssueLineID && l.IssueLineID === err.IssueLineID) || 
                    (l.ReceiveSourceId && l.ReceiveSourceId === err.ReceiveSourceId)
                );
                
                if (lineIndex !== -1) {
                    newLines[lineIndex].RemainingQty = err.RemainingQty;
                    newHighlightedRows.push(lineIndex);
                }
            });
            
            setLines(newLines);
            setHighlightedRows(newHighlightedRows);
            
            const errorMessages = error.errors.map(err => 
                `Line ${err.IssueLineNumber} (${err.ProductCode}): Requested ${err.RequestedQty}, Available ${err.RemainingQty}`
            ).join('\n');
            
            showToast('Please review the highlighted row', 'error', 'Quantity Exceeded');
        } else {
            showToast(error.message, 'error', 'Save Error');
        }
  }finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

    const showToast = (message, type = 'error', title = 'Validation Error', duration = 4000) => {
    setToast({ open: true, message, type, title });

    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  if (!open) return null;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            button:focus, select:focus, input:focus, textarea:focus { outline: 2px solid #4299e1; outline-offset: 2px; }
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type=number] {
              -moz-appearance: textfield;
            }
            .uppercase-input { text-transform: uppercase; }
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }

            @keyframes toastIn {
            from {
              opacity: 0;
              transform: translate(-50%, -16px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0) scale(1);
            }
          }

          @keyframes toastProgress {
            from { width: 100%; }
            to { width: 0%; }
          }

          .animate-toastIn {
            animation: toastIn 0.25s ease-out;
          }

          .animate-toastProgress {
            animation: toastProgress 4s linear forwards;
          }

          .highlight-row {
            animation: highlightPulse 2s ease-in-out;
          }

          @keyframes highlightPulse {
            0%, 100% { background-color: rgba(254, 226, 226, 0.5); }
            50% { background-color: rgba(254, 226, 226, 1); }
          }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                {(initialData && initialData.header && initialData.header.ReceiveHeaderID) ? 'Edit Material Receive' : 'Create Material Receive'}
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Material Receive Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receive No</label>
                    <input 
                      type="text" 
                      value={(initialData && initialData.header) ? header.ReceiveHeaderNo : nextReceiveNumber} 
                      disabled 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 font-medium" 
                    />
                  </div>

                  {/* NEW: Receive Type Dropdown */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receive Type <span className='text-red-600'>*</span></label>
                    <select 
                      value={receiveType} 
                      onChange={(e) => handleReceiveTypeChange(e.target.value)}
                      disabled={isHeaderLocked}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                    >
                      <option value="Issue">Issue</option>
                      <option value="Return">Material Return</option>
                    </select>
                  </div>

                  {/* Conditional Dropdown: Issue or Return */}
                  {receiveType === 'Issue' ? (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Material Issue <span className='text-red-600'>*</span></label>
                      <SearchableDropdown
                        options={materialIssues}
                        value={header.IssueHeaderID}
                        onChange={handleMaterialIssueChange}
                        disabled={isHeaderLocked}
                        placeholder="Select Material Issue..."
                        isLoading={filterLoading}
                        getOptionLabel={(mi) => `${mi.IssueHeaderNo} - ${mi.ProjectNo || ''} (${mi.Status || 'Open'})`}
                        getOptionValue={(mi) => mi.IssueHeaderID}
                        getOptionSecondaryLabel={(mi) => mi.IssueDate ? new Date(mi.IssueDate).toLocaleDateString() : ''}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">Material Return <span className='text-red-600'>*</span></label>
                      <SearchableDropdown
                        options={materialReturns}
                        value={header.ReceiveSourceId}
                        onChange={handleMaterialReturnChange}
                        disabled={isHeaderLocked}
                        placeholder="Select Material Return..."
                        isLoading={filterLoading}
                        getOptionLabel={(mr) => `${mr.MaterialReturnNo} - ${mr.ProjectNo || ''}`}
                        getOptionValue={(mr) => mr.MaterialReturnID}
                        getOptionSecondaryLabel={(mr) => mr.Status || ''}
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Site Indent</label>
                    <SearchableDropdown
                      options={siteIndents}
                      value={header.SiteIndentID}
                      onChange={(val) => setHeader({...header, SiteIndentID: val})}
                      disabled={isHeaderLocked || receiveType === 'Return'}
                      placeholder="Select Site Indent..."
                      getOptionLabel={(si) => `${si.SiteIndentNo} - ${si.ProjectNo || ''}`}
                      getOptionValue={(si) => si.SiteIndentID}
                      getOptionSecondaryLabel={(si) => si.Status || ''}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Survey BOQ</label>
                    <SearchableDropdown
                    options={surveyBOQs}
                    value={header.SurveyBOQID}
                    onChange={(val) => setHeader({...header, SurveyBOQID: val})}
                    disabled={isHeaderLocked }
                    placeholder="Select Survey BOQ..."
                    getOptionLabel={(b) => `${b.SurveyBOQNo} - ${b.ProjectCode || ''}`} 
                    getOptionValue={(b) => b.SurveyBOQID}
                    getOptionSecondaryLabel={(b) => b.Circle ? `${b.Circle} / ${b.District}` : ''}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receive Date</label>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={header.ReceiveDate ? dayjs(header.ReceiveDate) : null}
                        onChange={(newValue) => {
                          setHeader({
                            ...header,
                            ReceiveDate: newValue ? newValue.format('YYYY-MM-DD') : ''
                          });
                        }}
                        disabled={isHeaderLocked}
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            size: 'small',
                            className: 'w-full',
                            required: true
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </div>

                  {/* Approval/BC Status */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]"> Status</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
                      {!initialData ? (
                        <span className="text-xs font-medium text-gray-600 italic">Open</span>
                      ) : (header.PostingSiteIndent === 1 || header.PostingSiteIndent === true) ? (
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                          Material Received Successfully
                        </span>
                      ) : (header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? (
                        <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
                          Sent to BC (Ready to Post)
                        </span>
                      ) : header.ApprovalStatus === 'PENDING' ? (
                        <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                          Pending Approval (Level {header.CurrentLevel || 1})
                        </span>
                      ) : header.ApprovalStatus === 'REJECTED' ? (
                        <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                          Rejected
                        </span>
                      ) : header.ApprovalStatus === 'APPROVED' ? (
                        <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                          Approved (Ready to Send BC)
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 italic">Open</span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Vendor</label>
                    <SearchableDropdown
                      options={vendors}
                      value={header.VendorCode}
                      onChange={(val) => setHeader({...header, VendorCode: val})}
                       disabled={isHeaderLocked}
                      placeholder="Select Vendor"
                      getOptionLabel={(v) => v.vendor_name || v.VendorCode || v.name}
                      getOptionValue={(v) => v.vendor_code || v.VendorCode || v.code}
                      getOptionSecondaryLabel={(v) => v.vendor_code || v.VendorCode}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project</label>
                    <SearchableDropdown
                      options={projects}
                      value={header.ProjectNo}
                      onChange={(val) => setHeader({...header, ProjectNo: val})}
                       disabled={isHeaderLocked}
                      placeholder="Select Project"
                      getOptionLabel={(p) => `${p.No} - ${p.Description}`}
                      getOptionValue={(p) => p.No}
                      getOptionSecondaryLabel={(p) => p.No}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">From Feeder</label>
                    <SearchableDropdown
                      options={feeders}
                      value={header.FromFeederId}
                      onChange={(val) => setHeader({...header, FromFeederId: val})}
                       disabled={isHeaderLocked}
                      placeholder="Select Feeder"
                      getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
                      getOptionValue={(f) => f.FeederId}
                      getOptionSecondaryLabel={(f) => f.Code}
                    />
                  </div>
                    <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Activity</label>
                    <input 
                      type="text" 
                      value={header.ActivityName || ''} 
                      readOnly 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Activity</label>
                    <input 
                      type="text" 
                      value={header.SubActivityName || ''} 
                      readOnly 
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" 
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder</label>
                    <SearchableDropdown
                      options={feeders}
                      value={header.ToFeederId}
                      onChange={(val) => setHeader({...header, ToFeederId: val})}
                       disabled={isHeaderLocked}
                      placeholder="Select Feeder"
                      getOptionLabel={(f) => `${f.Code} - ${f.Description}`}
                      getOptionValue={(f) => f.FeederId}
                      getOptionSecondaryLabel={(f) => f.Code}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <input type="text" value={header.Circle} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <input type="text" value={header.District} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <input type="text" value={header.Division} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <input type="text" value={header.SubDivision} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <input type="text" value={header.SubStation} readOnly className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase-input" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receiver Name</label>
                    <input type="text" value={header.ReceiverName} onChange={(e)=>setHeader({...header, ReceiverName: e.target.value})} className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" />
                  </div>

                   <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                        Attachment <span className='text-red-600'></span>
                    </label>
                    <div className="flex flex-col items-start">
                        <input
                            type="file"
                            ref={headerFileInputRef}
                            onChange={handleHeaderFileChange}
                            className="hidden"
                            id="header-file-upload"
                        />
                        <label
                            htmlFor="header-file-upload"
                            className={`cursor-pointer flex items-center ${
                                !header.AttachmentFileName 
                                    ? 'text-blue-600 hover:text-blue-800' 
                                    : 'text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            <FaPaperclip className="mr-1" />
                            {header.AttachmentFileName ? (
                                <span className="text-xs truncate max-w-[150px]">
                                    {header.AttachmentFileName}
                                </span>
                            ) : (
                                <span className="text-xs">Attach File</span>
                            )}
                        </label>
                                        
                        {header.AttachmentFileName && (
                            <div className="flex mt-1 gap-2">
                                {header.ReceiveHeaderID && (
                                    <button
                                        type="button"
                                        onClick={handleDownloadHeaderAttachment}
                                        className="text-green-600 hover:text-green-800 text-xs"
                                        title="Download"
                                    >
                                        <FaDownload size={12} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleRemoveHeaderAttachment}
                                    className="text-red-600 hover:text-red-800 text-xs"
                                    title="Remove"
                                >
                                    <FaTrash size={12} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                  <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <textarea value={header.Remarks} onChange={e => setHeader({...header, Remarks: e.target.value})}  disabled={isHeaderLocked} rows="2" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"></textarea>
                  </div>

               
                 

                </div>
              </div>

              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#384959]">Receive Lines</h3>
                {highlightedRows.length > 0 && (
                    <button
                        type="button"
                        onClick={() => setHighlightedRows([])}
                        className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
                    >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Clear Highlights ({highlightedRows.length} rows)
        </button>
    )}
</div>
                
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0] w-full">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">Line No</ResizableTh>
                        <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>Product Code</ResizableTh>
                        <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>Task No</ResizableTh>
                        <ResizableTh width={columnWidths.desc} onResize={(x) => startResize('desc', x)}>Description</ResizableTh>
                        <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>UOM</ResizableTh>
                         <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                          Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                          Sub Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.siteQty} onResize={(x) => startResize('siteQty', x)} align="right">Indent Qty</ResizableTh>
                        <ResizableTh width={columnWidths.issuedQty} onResize={(x) => startResize('issuedQty', x)} align="right">Issued Qty</ResizableTh>
                        <ResizableTh width={columnWidths.qtyToReceive} onResize={(x) => startResize('qtyToReceive', x)} align="right">Qty To Receive</ResizableTh>
                        <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)}>
                          Remark
                        </ResizableTh>
                        
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, idx) => {
                        const isLineLocked =  line.BcSuccess === true || line.BcSuccess === 1;
                        const selectedActivityObj = projectActivities.find(a => a.ActivityId == line.ActivityId);
                        const activityDisplay = selectedActivityObj 
                          ? `${selectedActivityObj.Code} - ${selectedActivityObj.Description}` 
                          : '';

                        const selectedSubActivityObj = projectActivities.find(a => a.ActivityId == line.SubActivityId);
                        const subActivityDisplay = selectedSubActivityObj 
                          ? `${selectedSubActivityObj.Code} - ${selectedSubActivityObj.Description}` 
                          : '';

                      return(
                        <tr 
                        key={idx} 
                        className={`border-t border-[#E2E8F0] transition-colors duration-300 ${
                            highlightedRows.includes(idx) 
                                ? 'bg-red-100 border-l-4 border-l-red-500' 
                                : ''
                        }`}
                    >
                          <td className="p-2 text-center" style={{width: columnWidths.lineNo}}>
                            <input type="number" value={line.IssueLineNumber} onChange={e => handleLineChange(idx, 'IssueLineNumber', e.target.value)} className="w-16 border border-[#E2E8F0] rounded-lg px-2 py-1 text-center focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.productCode}}>
                            <input type="text" value={line.ProductCode} onChange={e => handleLineChange(idx, 'ProductCode', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase-input" readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.taskNo}}>
                            <input type="text" value={line.TaskNo} onChange={e => handleLineChange(idx, 'TaskNo', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                            readOnly />
                          </td>
                          <td className="p-2" style={{width: columnWidths.desc}}>
                            <input type="text" value={line.Description} onChange={e => handleLineChange(idx, 'Description', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" readOnly/>
                          </td>
                          <td className="p-2" style={{width: columnWidths.uom}}>
                            <input type="text" value={line.UnitOfMeasure} onChange={e => handleLineChange(idx, 'UnitOfMeasure', e.target.value)} className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 uppercase-input" readOnly/>
                          </td>
                          <td className="p-2" style={{ width: columnWidths.activity }}>
                            <input 
                              type="text" 
                              value={activityDisplay}
                              readOnly 
                              disabled
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                            />
                          </td>
                           <td className="p-2" style={{ width: columnWidths.subActivity }}>
                            <input 
                              type="text" 
                              value={subActivityDisplay}
                              readOnly 
                              disabled
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] disabled:bg-gray-100 pointer-events-none" 
                            />
                          </td>
                          <td className="p-2" style={{width: columnWidths.siteQty}}>
                            <input type="number" inputMode="decimal"  step="0.000001" value={line.SiteIndentQty} disabled className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right bg-gray-50 text-gray-500" readOnly/>
                          </td>
                          <td className="p-2" style={{width: columnWidths.issuedQty}}>
                            <input type="number" inputMode="decimal"  step="0.000001" value={line.IssuedQty} disabled className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right bg-gray-50 text-gray-500" readOnly/>
                          </td>
                          <td className="p-2" style={{width: columnWidths.qtyToReceive}}>
                            <input type="text"
                           inputMode="decimal"  step="0.000001" value={line.QtyToReceive} onChange={e => handleLineChange(idx, 'QtyToReceive', e.target.value)}
                            disabled={isLineLocked}
                            onFocus={(e) => e.target.select()}
                             className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 text-right focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]" />
                          </td>
                          <td className="p-2" style={{width: columnWidths.remark}}>
                            <input 
                              type="text"
                              value={line.Remark || ''}
                              onChange={e => handleLineChange(idx, 'Remark', e.target.value)}
                              className="w-full border border-[#E2E8F0] rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                              // No 'readOnly' attribute here so user can edit
                            />
                          </td>
                          
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines.length}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Qty To Receive</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {lines.reduce((acc, line) => acc + (parseFloat(line.QtyToReceive) || 0), 0).toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button type="button" className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed flex items-center" 
                disabled={loading || isSubmitting} 
                onClick={handleSubmit}
              >
                {loading || isSubmitting ? 'Saving...' : (
                  <>
                    <SaveIcon className="mr-2" size={18} />
                    {(initialData && initialData.header && initialData.header.ReceiveHeaderID) ? 'Update Receive' : 'Save Receive'}
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>


            {/* --- TOP CENTER TOAST --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div
            className={`
              relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
              ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
              ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
              ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
            `}
          >
            {/* Content */}
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}

                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">
                  {toast.title}
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {toast.message}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MaterialReceiveFormModal;